import {
  GenericTable,
  GenericTableVariant,
  Input,
  Modal,
  NewButton,
  NewButtonVariant,
  Tag,
  TagSize,
  TagType,
  TagVariant,
  TooltipTrigger,
} from "@visiblethread/react-components";
import {
  EBUY_TYPE,
  FIND_A_TENDER_TYPE,
  MANUAL_TYPE,
  OPPORTUNITY_SOURCES,
  OPPORTUNITY_TYPES,
  SAM_GOV_TYPE,
} from "constants/opportunities";
import { modalTypes } from "constants/types";
import { FileTextIcon, SearchIcon } from "lucide-react";
import { func } from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";
import { fetchTrackedOppsAsync } from "slices/opportunity/tracked/actions";
import { startProposalAndNavigate } from "slices/proposals/actions";

import classes from "./TrackedOpportunityPickerModal.module.scss";

const PROPOSAL_EXISTS_TOOLTIP =
  "A proposal already exists for this opportunity";

const SOURCE_TAG_TYPE = {
  [SAM_GOV_TYPE]: TagType.Blue,
  [EBUY_TYPE]: TagType.Yellow,
  [FIND_A_TENDER_TYPE]: TagType.Green,
  [MANUAL_TYPE]: TagType.Gray,
};

const sanitize = (str = "") => str.toLowerCase().replace(/[.,_\-?!]+/gi, "");

const matchesSearch = (opp, term) => {
  if (!term) return true;
  const s = sanitize(term);
  return (
    sanitize(opp.title ?? "").includes(s) ||
    sanitize(opp.externalId ?? "").includes(s)
  );
};

const TrackedOpportunityPickerModal = ({ onBack }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    dispatch(fetchTrackedOppsAsync())
      .unwrap()
      .then((data) => {
        if (!cancelled) {
          setOpportunities(data ?? []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const filteredData = useMemo(
    () => opportunities.filter((opp) => matchesSearch(opp, searchTerm)),
    [opportunities, searchTerm]
  );

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "title",
        id: "title",
        enableSorting: true,
        cell: ({ row }) => {
          const { title, proposalStarted } = row.original;
          return proposalStarted ? (
            <TooltipTrigger content={PROPOSAL_EXISTS_TOOLTIP}>
              <span className={classes.tooltipCell}>{title}</span>
            </TooltipTrigger>
          ) : (
            <span>{title}</span>
          );
        },
      },
      {
        header: "Proposal?",
        accessorKey: "proposalStarted",
        id: "proposalStarted",
        enableSorting: true,
        size: 90,
        cell: ({ row }) => {
          if (!row.original.proposalStarted) return null;
          return (
            <TooltipTrigger content={PROPOSAL_EXISTS_TOOLTIP}>
              <span className={classes.tooltipCell}>
                <FileTextIcon width={16} height={16} />
              </span>
            </TooltipTrigger>
          );
        },
      },
      {
        header: "Source",
        accessorKey: "opportunitySource",
        id: "opportunitySource",
        enableSorting: true,
        size: 110,
        cell: ({ row }) => {
          const { opportunitySource, proposalStarted } = row.original;
          const label = OPPORTUNITY_SOURCES[opportunitySource];
          const tag = label ? (
            <Tag
              text={label}
              type={SOURCE_TAG_TYPE[opportunitySource] ?? TagType.Gray}
              size={TagSize.Small}
              variant={TagVariant.Highlight}
            />
          ) : null;
          return proposalStarted ? (
            <TooltipTrigger content={PROPOSAL_EXISTS_TOOLTIP}>
              <span className={classes.tooltipCell}>{tag}</span>
            </TooltipTrigger>
          ) : (
            tag
          );
        },
      },
      {
        header: "Type",
        accessorKey: "opportunityType",
        id: "opportunityType",
        enableSorting: true,
        size: 110,
        cell: ({ row }) => {
          const { opportunityType, proposalStarted } = row.original;
          const value = OPPORTUNITY_TYPES[opportunityType] ?? "—";
          return proposalStarted ? (
            <TooltipTrigger content={PROPOSAL_EXISTS_TOOLTIP}>
              <span className={classes.tooltipCell}>{value}</span>
            </TooltipTrigger>
          ) : (
            value
          );
        },
      },
      {
        header: "Buyer",
        accessorFn: (row) => row.customer ?? row.departmentAgency ?? "",
        id: "departmentAgency",
        enableSorting: true,
        cell: ({ row }) => {
          const { customer, departmentAgency, proposalStarted } = row.original;
          const text = customer ?? departmentAgency ?? "—";
          return proposalStarted ? (
            <TooltipTrigger content={PROPOSAL_EXISTS_TOOLTIP}>
              <span className={classes.tooltipCell} title={text}>
                {text}
              </span>
            </TooltipTrigger>
          ) : (
            <span title={text}>{text}</span>
          );
        },
      },
    ],
    []
  );

  const isRowDisabled = (row) => row.proposalStarted;

  const onRowClick = (row) => {
    if (isRowDisabled(row)) return;
    setSelectedOpportunityId(row.id);
  };

  const handleCreateProposal = () => {
    dispatch(startProposalAndNavigate(selectedOpportunityId, navigate));
  };

  return (
    <Modal
      data-testid="tracked-opportunity-picker-modal"
      className={`fade-in ${classes.modal}`}
      show
      centered
      size="lg"
      backdrop
      onDismiss={() =>
        dispatch(hideModal(modalTypes.TRACKED_OPPORTUNITY_PICKER))
      }
    >
      <Modal.Header>From a Tracked Opportunity</Modal.Header>

      <Modal.Body>
        <Input
          className={classes.search}
          data-testid="picker-search"
          onChange={setSearchTerm}
          placeholder="Search by name or solicitation number"
          prefix={<SearchIcon size={16} />}
          value={searchTerm}
        />
        <div className={classes.tableWrapper}>
          <GenericTable
            columns={columns}
            data={filteredData}
            defaultSortColumnIndex={0}
            isRowDisabled={isRowDisabled}
            loading={isLoading}
            onRowClick={onRowClick}
            variant={GenericTableVariant.bordered}
          />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l3} onClick={onBack}>
          Back
        </NewButton>
        <NewButton
          variant={NewButtonVariant.l1}
          disabled={selectedOpportunityId === null}
          onClick={handleCreateProposal}
        >
          Create Proposal
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

TrackedOpportunityPickerModal.propTypes = {
  onBack: func.isRequired,
};

export default TrackedOpportunityPickerModal;
